import { storage, auth } from '../config/firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { database } from '../config/firebase';
import { ref as dbRef, set, push, update, remove, query, orderByChild, equalTo, get } from 'firebase/database';

export interface Update {
  id: string;
  deviceId: string;
  apkUrl: string;
  status: 'pending' | 'installing' | 'installed' | 'failed';
  createdAt: number;
  updatedAt: number;
  updateType?: 'update' | 'newInstall';
}

export const updateService = {
  uploadApk: async (deviceId: string, file: File, onProgress?: (progress: number) => void): Promise<{ apkUrl: string }> => {
    // Verificar autenticação
    if (!auth.currentUser) {
      throw new Error('Usuário não está autenticado');
    }

    // Criar referência para o arquivo
    const fileName = `${Date.now()}_${file.name}`;
    const storageRef = ref(storage, `apks/${deviceId}/${fileName}`);

    // Configurar metadados do upload conforme a documentação
    const metadata = {
      contentType: 'application/vnd.android.package-archive'
    };

    // Iniciar o upload usando o método recomendado na documentação
    const uploadTask = uploadBytesResumable(storageRef, file, metadata);

    // Criar uma promise para monitorar o upload
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        // Callback de progresso
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`Upload em progresso: ${progress}%`);
          onProgress?.(progress);
        },
        // Callback de erro
        (error) => {
          console.error('Erro no upload:', error);

          // Tratamento específico de erros conforme a documentação
          switch (error.code) {
            case 'storage/unauthorized':
              reject(new Error('Usuário não tem permissão para enviar este arquivo'));
              break;
            case 'storage/canceled':
              reject(new Error('Upload cancelado'));
              break;
            case 'storage/unknown':
              console.error('Resposta do servidor:', error.serverResponse);
              reject(new Error(`Erro desconhecido: ${error.message}`));
              break;
            default:
              reject(error);
          }
        },
        // Callback de conclusão - apenas retorna a URL, sem criar registro no /updates/
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log('Download disponível em:', downloadURL);
            resolve({ apkUrl: downloadURL });
          } catch (finalError) {
            console.error('Erro ao finalizar upload:', finalError);
            reject(finalError);
          }
        }
      );
    });
  },

  assignApkToDevice: async (deviceId: string, apkUrl: string, updateType: 'update' | 'newInstall' = 'update'): Promise<void> => {
    // Verificar autenticação
    if (!auth.currentUser) {
      throw new Error('Usuário não está autenticado');
    }
    
    try {
      // 1. Atualizar o campo apkUrl no dispositivo
      const deviceRef = dbRef(database, `devices/${deviceId}`);
      await update(deviceRef, {
        apkUrl: apkUrl
      });

      // 2. Criar uma nova entrada de atualização para o dispositivo
      const updateRef = push(dbRef(database, 'updates'));
      
      // Criar objeto de atualização
      const updateObj: Update = {
        id: updateRef.key || Date.now().toString(),
        deviceId,
        apkUrl: apkUrl,
        status: 'pending',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        updateType
      };

      // Salvar no database
      await set(updateRef, updateObj);
      
    } catch (error) {
      console.error(`Erro ao associar APK ao dispositivo ${deviceId}:`, error);
      throw error;
    }
  },
  
  cancelUpdate: async (updateId: string, deviceId: string): Promise<boolean> => {
    if (!auth.currentUser) {
      throw new Error('Usuário não está autenticado');
    }

    try {
      // Remover o registro de update
      await remove(dbRef(database, `updates/${updateId}`));

      // Limpar flag do dispositivo
      await update(dbRef(database, `devices/${deviceId}`), {
        updateRequested: false,
        apkUrl: null
      });

      console.log(`Atualização ${updateId} cancelada para dispositivo ${deviceId}`);
      return true;
    } catch (error) {
      console.error(`Erro ao cancelar atualização ${updateId}:`, error);
      throw error;
    }
  },

  cancelPendingUpdates: async (deviceId: string): Promise<boolean> => {
    // Verificar autenticação
    if (!auth.currentUser) {
      throw new Error('Usuário não está autenticado');
    }
    
    try {
      // 1. Consultar TODAS as atualizações para o dispositivo (não apenas pendentes)
      const updatesRef = dbRef(database, 'updates');
      const deviceUpdatesQuery = query(
        updatesRef,
        orderByChild('deviceId'),
        equalTo(deviceId)
      );
      
      const snapshot = await get(deviceUpdatesQuery);
      
      if (!snapshot.exists()) {
        return false;
      }
      
      let updateCount = 0;
      
      // 2. Excluir TODAS as atualizações do dispositivo, independente do status
      const updates: Promise<void>[] = [];
      
      snapshot.forEach((childSnapshot) => {
        // Removemos todos os registros de atualização, não apenas os pendentes
        updates.push(remove(dbRef(database, `updates/${childSnapshot.key}`)));
        updateCount++;
      });
      
      if (updates.length > 0) {
        await Promise.all(updates);
        
        // 3. Remover a flag updateRequested e a URL do APK do dispositivo
        await update(dbRef(database, `devices/${deviceId}`), {
          updateRequested: false,
          apkUrl: null // Também remove a URL do APK quando a atualização é cancelada
        });
        
        console.log(`Removidas ${updateCount} atualizações do dispositivo ${deviceId}`);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error(`Erro ao cancelar atualizações para o dispositivo ${deviceId}:`, error);
      throw error;
    }
  }
}; 